import { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy';
import { MdExpandMore } from '@react-icons/all-files/md/MdExpandMore';
import { MdExpandLess } from '@react-icons/all-files/md/MdExpandLess';
import { McpSkill, MCP_SKILLS, skillPath } from './mcpSkills';
import { scopeLabel } from './mcpScopes';

type McpSkillsSectionProps = {
    /** Copy handler owned by the parent, so clipboard failures raise one toast style. */
    onCopy: (value: string, label: string) => void;
};

/** The directory a skill has to land in for Claude Code to pick it up. */
const installSnippet = (skill: McpSkill): string =>
    `mkdir -p ~/.claude/skills/${skill.name}\n# paste the skill into ~/.claude/skills/${skill.name}/SKILL.md`;

/**
 * Catalog of Amplifier-authored skills a customer copies into their own Claude
 * configuration (APD-1818).
 *
 * A skill is plain markdown, so this is a copy surface rather than an install
 * flow — nothing here calls the API, and nothing here is tenant-specific. The
 * scope chips exist because a skill whose tools the token cannot reach will
 * fail at use time, and the failure reads as a broken skill rather than a
 * missing grant.
 */
export const McpSkillsSection = ({ onCopy }: McpSkillsSectionProps) => {
    const [expanded, setExpanded] = useState<string | null>(null);

    const toggle = useCallback((name: string) => {
        setExpanded((current) => (current === name ? null : name));
    }, []);

    return (
        <div className="settings-v2__card settings-v2__mcp-skills">
            <div className="settings-v2__card-header">
                <h3 className="settings-v2__card-title">Skills for your model</h3>
                <p className="settings-v2__card-subtitle">
                    A skill teaches your model how to use Amplifier well: which tool to reach for, what to check first,
                    and what not to claim. Save one as <code>SKILL.md</code> in your skills directory and your client
                    loads it when the work matches.
                </p>
            </div>

            <div className="settings-v2__mcp-skill-rows">
                {MCP_SKILLS.map((skill) => {
                    const isOpen = expanded === skill.name;
                    return (
                        <div className="settings-v2__mcp-skill" key={skill.name}>
                            <div className="settings-v2__mcp-skill-head">
                                <div className="settings-v2__mcp-skill-meta">
                                    <span className="settings-v2__setting-label">{skill.title}</span>
                                    <p className="settings-v2__setting-sublabel">{skill.summary}</p>
                                    <div className="settings-v2__mcp-skill-tags">
                                        <code className="settings-v2__mcp-skill-path">{skillPath(skill)}</code>
                                        {skill.scopes.map((scope) => (
                                            <span className="settings-v2__mcp-skill-scope" key={scope}>
                                                {scopeLabel(scope)}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="settings-v2__mcp-skill-actions">
                                    <Button
                                        variant="link"
                                        className="settings-v2__mcp-skill-toggle"
                                        aria-expanded={isOpen}
                                        onClick={() => toggle(skill.name)}
                                    >
                                        {isOpen ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
                                        {isOpen ? 'Hide' : 'Preview'}
                                    </Button>
                                    <Button
                                        className="settings-v2__mcp-btn"
                                        onClick={() => onCopy(skill.markdown, `${skill.title} skill`)}
                                    >
                                        <MdContentCopy size={16} /> Copy skill
                                    </Button>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="settings-v2__mcp-skill-body">
                                    <pre
                                        className="settings-v2__mcp-snippet settings-v2__mcp-skill-install"
                                        data-testid={`mcp-skill-install-${skill.name}`}
                                    >
                                        {installSnippet(skill)}
                                    </pre>
                                    <pre
                                        className="settings-v2__mcp-snippet settings-v2__mcp-skill-markdown"
                                        data-testid={`mcp-skill-markdown-${skill.name}`}
                                    >
                                        {skill.markdown}
                                    </pre>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default McpSkillsSection;
